import Phaser from 'phaser';
import { AnimationKeys, AudioKeys, SceneKeys, TextureKeys } from '../config/keys';
import { AudioManager } from '../managers/AudioManager';

export class BootScene extends Phaser.Scene {
  constructor() {
    super(SceneKeys.Boot);
  }

  preload(): void {
    const assetBase = import.meta.env.BASE_URL;

    this.load.image(TextureKeys.BirdFrame1, `${assetBase}assets/sprites/bird/flappy1.png`);
    this.load.image(TextureKeys.BirdFrame2, `${assetBase}assets/sprites/bird/flappy2.png`);

    this.load.audio(AudioKeys.Flap, `${assetBase}assets/audio/flap.mp3`);
    this.load.audio(AudioKeys.Score, `${assetBase}assets/audio/score.mp3`);
    this.load.audio(AudioKeys.Hit, `${assetBase}assets/audio/hit.mp3`);
    this.load.audio(AudioKeys.GameOver, `${assetBase}assets/audio/gameOver.mp3`);
    this.load.audio(AudioKeys.MenuSelect, `${assetBase}assets/audio/menuSelect.mp3`);
  }

  create(): void {
    this.createGeneratedTextures();

    this.anims.create({
      key: AnimationKeys.BirdFlap,
      frames: [
        { key: TextureKeys.BirdFrame1 },
        { key: TextureKeys.BirdFrame2 },
      ],
      frameRate: 8,
      repeat: -1,
    });

    AudioManager.get(this).registerLoadedSounds();
    this.scene.start(SceneKeys.Menu);
  }

  private createGeneratedTextures(): void {
    this.createPaperBackground();
    this.createObstacleTexture(TextureKeys.ObstacleTop, true);
    this.createObstacleTexture(TextureKeys.ObstacleBottom, false);
    this.createGroundTexture();
    this.createCloudTexture();
  }

  private createPaperBackground(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 }, false);
    graphics.fillStyle(0xf7f0dc, 1);
    graphics.fillRect(0, 0, 390, 844);

    graphics.lineStyle(1, 0xb6d6e9, 0.42);
    for (let y = 46; y < 844; y += 34) {
      graphics.beginPath();
      graphics.moveTo(0, y + Phaser.Math.Between(-1, 1));
      graphics.lineTo(390, y + Phaser.Math.Between(-1, 1));
      graphics.strokePath();
    }

    graphics.lineStyle(2, 0xef8d8d, 0.34);
    graphics.lineBetween(48, 0, 48, 844);

    graphics.fillStyle(0x000000, 0.08);
    [96, 370, 650].forEach((y) => {
      graphics.fillCircle(22, y, 8);
      graphics.fillStyle(0xf7f0dc, 1);
      graphics.fillCircle(22, y, 5);
      graphics.fillStyle(0x000000, 0.08);
    });

    graphics.generateTexture(TextureKeys.PaperBackground, 390, 844);
    graphics.destroy();
  }

  private createObstacleTexture(key: string, pointsDown: boolean): void {
    const graphics = this.make.graphics({ x: 0, y: 0 }, false);
    const width = 82;
    const height = 420;
    const capY = pointsDown ? height - 34 : 0;
    const bodyY = pointsDown ? 0 : 34;

    graphics.fillStyle(0xd8ead0, 1);
    graphics.lineStyle(4, 0x28221a, 0.92);
    graphics.fillRoundedRect(12, bodyY, 58, height - 34, 8);
    graphics.strokeRoundedRect(12, bodyY, 58, height - 34, 8);
    graphics.fillRoundedRect(2, capY, 78, 34, 8);
    graphics.strokeRoundedRect(2, capY, 78, 34, 8);

    graphics.lineStyle(2, 0x4f8f57, 0.55);
    for (let i = 0; i < 7; i += 1) {
      const y = bodyY + 26 + i * 46;
      graphics.beginPath();
      graphics.moveTo(22 + Phaser.Math.Between(-2, 2), y);
      graphics.lineTo(62 + Phaser.Math.Between(-2, 2), y + Phaser.Math.Between(-3, 3));
      graphics.strokePath();
    }

    graphics.lineStyle(2, 0x28221a, 0.62);
    graphics.beginPath();
    graphics.moveTo(16, bodyY + 14);
    graphics.lineTo(68, bodyY + height - 58);
    graphics.strokePath();

    graphics.generateTexture(key, width, height);
    graphics.destroy();
  }

  private createGroundTexture(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 }, false);
    graphics.fillStyle(0xead8a8, 1);
    graphics.fillRect(0, 0, 390, 74);
    graphics.lineStyle(4, 0x28221a, 0.78);
    graphics.beginPath();
    graphics.moveTo(0, 8);
    for (let x = 0; x <= 390; x += 18) {
      graphics.lineTo(x, 8 + Phaser.Math.Between(-3, 4));
    }
    graphics.strokePath();

    graphics.lineStyle(2, 0x8c6d3c, 0.4);
    for (let x = 8; x < 390; x += 28) {
      graphics.lineBetween(x, 28, x + 12, 35 + Phaser.Math.Between(-2, 3));
    }

    graphics.generateTexture(TextureKeys.Ground, 390, 74);
    graphics.destroy();
  }

  private createCloudTexture(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 }, false);
    graphics.lineStyle(3, 0x28221a, 0.38);
    graphics.fillStyle(0xffffff, 0.42);
    graphics.fillEllipse(42, 30, 72, 30);
    graphics.strokeEllipse(42, 30, 72, 30);
    graphics.fillEllipse(74, 28, 48, 24);
    graphics.strokeEllipse(74, 28, 48, 24);
    graphics.fillEllipse(20, 34, 38, 18);
    graphics.strokeEllipse(20, 34, 38, 18);
    graphics.generateTexture(TextureKeys.Cloud, 104, 58);
    graphics.destroy();
  }
}
